import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/common/Button';
import { SERVICE_FEES } from '../utils/constants';
import useApplicationStore from '../stores/applicationStore';
import useAuthStore from '../stores/authStore';

const ServiceFeeSelection = () => {
  const [selectedOption, setSelectedOption] = useState(null);
  const navigate = useNavigate();
  const { selectedServiceFee, setSelectedServiceFee } = useApplicationStore();
  const { profile, isAuthenticated } = useAuthStore();

  useEffect(() => {
    // Redirect if not authenticated or profile not complete
    if (!isAuthenticated || !profile?.profileCompleted) {
      navigate('/profile-step-1');
      return;
    }

    // Restore previously selected option if exists
    if (selectedServiceFee) {
      setSelectedOption(selectedServiceFee);
    }
  }, [isAuthenticated, profile, selectedServiceFee, navigate]);

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };

  const handleContinue = () => {
    if (!selectedOption) return;
    
    // Store selected option in application store
    setSelectedServiceFee(selectedOption);
    navigate('/payment-instructions');
  };

  const handleBack = () => {
    navigate('/loan-limits');
  };

  return (
    <div className="page-container">
      <div className="form-container max-w-lg">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Select Loan Amount
          </h1>
          <p className="text-gray-600">
            Choose your loan amount and pay the service fee
          </p>
        </div>

        {/* User Profile Context */}
        {/*<div className="bg-primary-50 rounded-xl p-4 mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
              <span className="text-primary-600 font-semibold">
                {profile?.fullName?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-medium text-primary-900">{profile?.fullName}</p>
              <p className="text-sm text-primary-600">
                Monthly Income: KES {profile?.monthlyIncome?.toLocaleString()}
              </p>
            </div>
          </div>
        </div>*/}

        <div className="bg-yellow-50 rounded-xl p-4 mb-6">
          <div className="flex items-start">
            <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <p className="text-yellow-800 text-sm font-medium">
                Processing fee must be paid before loan approval
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3 mb-8">
          {SERVICE_FEES.map((option, index) => {
            // Calculate affordability indicator
            const monthlyIncome = profile?.monthlyIncome || 0;
            const isAffordable = monthlyIncome > 0 && option.loanAmount <= (monthlyIncome * 2);
            
            return (
              <div
                key={index}
                onClick={() => handleOptionSelect(option)}
                className={`border rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                  selectedOption?.fee === option.fee
                    ? 'border-primary-500 bg-primary-50 shadow-md'
                    : 'border-gray-300 hover:border-primary-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-lg font-bold text-gray-900">
                        Kes {option.loanAmount.toLocaleString()}
                      </span>
                      <div className="flex items-center gap-2">
                        {isAffordable && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                            Recommended
                          </span>
                        )}
                        {/*<span className="text-sm font-medium text-primary-600 bg-primary-100 px-2 py-1 rounded">
                          Fee: KES {option.fee}
                        </span>*/}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      {option.description}
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className={`w-Service5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedOption?.fee === option.fee
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedOption?.fee === option.fee && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="flex-1"
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            type="button"
            size="lg"
            className="flex-1"
            disabled={!selectedOption}
            onClick={handleContinue}
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServiceFeeSelection;